import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { type ControlSize, sizeClasses } from '@schmancy/theme/control-size'
import '@schmancy/primitives/icons/icon'

/**
 * Suggestion chip — a contextual recommendation that triggers an action when clicked.
 *
 * IMPORTANT: Suggestion chips do NOT have a selected state, unlike filter chips —
 * they cannot be toggled on/off.
 */
@customElement('schmancy-suggestion-chip')
export class SchmancySuggestionChip extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-block;
				outline: none;
				width: fit-content;
				min-width: fit-content;
			}

			:host([disabled]) {
				pointer-events: none;
			}

			.ripple {
				position: absolute;
				border-radius: var(--schmancy-radius-circle, 50%);
				transform: scale(0);
				animation: ripple 600ms linear;
				background-color: rgba(0, 0, 0, 0.08);
				pointer-events: none;
			}

			@keyframes ripple {
				to {
					transform: scale(4);
					opacity: 0;
				}
			}

			/* State layer for M3 hover/focus/pressed states */
			.state-layer {
				position: absolute;
				inset: 0;
				border-radius: inherit;
				pointer-events: none;
				background-color: currentColor;
				opacity: 0;
				transition: opacity 200ms ease;
			}

			:host(:not([disabled])) button:hover .state-layer {
				opacity: 0.08;
			}

			:host(:not([disabled])) button:focus-visible .state-layer {
				opacity: 0.1;
			}

			:host(:not([disabled])) button:active .state-layer {
				opacity: 0.1;
			}
		`,
	]
	@property({ reflect: true }) value = ''

	/** Material Symbols icon name. */
	@property({ reflect: true }) icon = ''

	@property({ reflect: true }) href = ''

	@property({ reflect: true }) target = ''

	@property({ type: Boolean, reflect: true }) disabled = false

	/** Flat by default per M3 spec. */
	@property({ type: Boolean, reflect: true }) elevated = false

	/** Size from the shared control-size ladder. Default 'sm' = 40px / 14px. */
	@property({ reflect: true }) size: ControlSize = 'sm'

	private hover$ = new BehaviorSubject<boolean>(false)
	private pressed$ = new BehaviorSubject<boolean>(false)
	private focused$ = new BehaviorSubject<boolean>(false)

	// Only ripples drive rendering; hover/pressed/focused stay outside @state.
	@state() private ripples: Array<{ x: number; y: number; id: number }> = []

	protected static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }

	private nextRippleId = 0

	connectedCallback() {
		super.connectedCallback()

		combineLatest([this.hover$, this.pressed$, this.focused$]).pipe(takeUntil(this.disconnecting)).subscribe()
	}

	private handleClick = (e: MouseEvent) => {
		if (this.disabled) return

		const button = this.shadowRoot?.querySelector('button')
		if (button) {
			const rect = button.getBoundingClientRect()
			const x = e.clientX - rect.left
			const y = e.clientY - rect.top
			const id = this.nextRippleId++

			this.ripples = [...this.ripples, { x, y, id }]

			setTimeout(() => {
				this.ripples = this.ripples.filter(r => r.id !== id)
			}, 600)
		}

		if (this.href) {
			if (this.target === '_blank') {
				window.open(this.href, '_blank')
			} else {
				window.location.href = this.href
			}
		}

		this.dispatchEvent(
			new CustomEvent('action', {
				detail: { value: this.value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleKeyDown = (e: KeyboardEvent) => {
		if (this.disabled) return

		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			this.pressed$.next(true)

			const clickEvent = new MouseEvent('click', {
				bubbles: true,
				cancelable: true,
				clientX: 0,
				clientY: 0,
			})
			this.handleClick(clickEvent)

			setTimeout(() => this.pressed$.next(false), 100)
		}
	}

	private handleFocus = () => {
		this.focused$.next(true)
	}

	private handleBlur = () => {
		this.focused$.next(false)
	}

	protected render(): unknown {
		const classes = {
			relative: true,
			'inline-flex': true,
			'items-center': true,
			'rounded-full': true,
			'cursor-pointer': !this.disabled,
			'transition-colors': true,
			'duration-200': true,
			'select-none': true,
			'overflow-hidden': true,

			// Size ladder: height / font / horizontal-pad / gap from control-size
			[sizeClasses(this.size, 'chip')]: true,

			'bg-surface-containerLow': true,
			'text-surface-onVariant': true,

			// Suggestion chips are flat by default (no elevation per M3)

			'focus-visible:outline-solid': !this.disabled,
			'focus-visible:outline-2': !this.disabled,
			'focus-visible:outline-primary': !this.disabled,
			'focus-visible:outline-offset-2': !this.disabled,

			'opacity-38': this.disabled, // M3 disabled opacity
			'cursor-not-allowed': this.disabled,
		}

		return html`
			<button
				type="button"
				class=${this.classMap(classes)}
				?disabled=${this.disabled}
				@click=${this.handleClick}
				@keydown=${this.handleKeyDown}
				@mouseenter=${() => this.hover$.next(true)}
				@mouseleave=${() => this.hover$.next(false)}
				@mousedown=${() => this.pressed$.next(true)}
				@mouseup=${() => this.pressed$.next(false)}
				@focus=${this.handleFocus}
				@blur=${this.handleBlur}
				tabindex=${this.disabled ? '-1' : '0'}
				role="button"
				aria-disabled=${this.disabled}
				aria-label=${this.ariaLabel || this.value}
			>
				${this.icon ? html` <schmancy-icon class="text-[18px] shrink-0">${this.icon}</schmancy-icon> ` : ''}
				<span class="font-medium">
					<slot></slot>
				</span>

				${this.ripples.map(
					ripple => html` <span class="ripple" style="left: ${ripple.x}px; top: ${ripple.y}px;"></span> `,
				)}

				<div class="state-layer"></div>
			</button>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-suggestion-chip': SchmancySuggestionChip
	}
}

export type SuggestionChipActionEvent = { value: string }
